import { actionAlert, Button, toast, Typography } from "@suraasa/placebo-ui"
import api from "api"
import {
  Article,
  LearningItem,
  VideoBookmark,
} from "api/resources/learningItems/types"
import { APIError } from "api/utils"
import { AuthManager } from "features/Authentication/AuthManager"
import groupBy from "lodash/groupBy"
import { useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { learningItemSlugs } from "utils/constants"
import { mapErrorsV2 } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { useLaunchPracticeInterview } from "./hooks/useLaunchPracticeInterview"
import {
  buildSyntheticItems,
  deriveSyntheticItemStatuses,
  getLearningContentTypeDisplay,
  isSyntheticItemId,
  SYNTHETIC_ITEM_IDS,
} from "./utils"

export type SwitchItemOptions =
  | {
      mode: "next" | "previous"
    }
  | {
      mode: "jump"
      userPlannerItemId: number
    }

const IS_EMPTY_DEFAULT_STATE = { list: false, item: false }

export const usePlanner = () => {
  const { slug } = useParams() as { slug: string }
  const { enrollments, cpat } = useEnrollments()
  const courseId = enrollments?.find(x => x.slug === slug)?.uuid

  const [searchParams] = useSearchParams()
  const plannerId = searchParams.get("plannerId")

  const itemIdToStartFrom = searchParams.get("itemId")

  const [planner, setPlanner] = useState<LearningItem[]>()
  const [isEmpty, setIsEmpty] = useState(IS_EMPTY_DEFAULT_STATE)
  const [loading, setLoading] = useState(false)

  const isBEv2Module = slug === learningItemSlugs.boostingEmployabilityV2

  const launchQuery = useLaunchPracticeInterview(
    Boolean(isBEv2Module && planner && planner.length > 0)
  )

  const [showPlannerCompletedSheet, setShowPlannerCompletedSheet] =
    useState(false)

  const [currentItem, setCurrentItem] = useState<LearningItem>()
  const [milestoneInstructions, setMilestoneInstructions] =
    useState<Article | null>(null)

  const milestones = useMemo(() => groupBy(planner, "milestone.id"), [planner])

  const totalMilestones = Object.keys(milestones).length
  const currentMilestoneId = planner?.find(x => x.id === currentItem?.id)
    ?.milestone.id

  const currentMilestoneNumber =
    Object.keys(milestones).findIndex(
      x => x === currentMilestoneId?.toString()
    ) + 1

  const isFirstItem =
    planner?.find(item => item.id === currentItem?.id)?.sequence === 1

  useEffect(() => {
    const getPlanner = async (id: string) => {
      try {
        setIsEmpty(IS_EMPTY_DEFAULT_STATE)
        const items = await api.learningItems.listPlannerItems({
          urlParams: { plannerId: id },
        })

        const finalItems =
          isBEv2Module && items.length > 0
            ? [...items, ...buildSyntheticItems(items)]
            : items

        setPlanner(finalItems)

        // Deep-link onto a synthetic item: it has no backend record, so it
        // can't be resolved via `getItem`'s API calls below. `getItem` skips
        // sentinel ids entirely to avoid a race with this.
        if (itemIdToStartFrom && isSyntheticItemId(Number(itemIdToStartFrom))) {
          const match = finalItems.find(i => i.id === Number(itemIdToStartFrom))
          if (match) setCurrentItem(match)
        }
      } catch (error) {
        setIsEmpty(p => ({ ...p, list: true }))
        console.error(error)
      }
    }

    const getItem = async (plannerId: string) => {
      if (itemIdToStartFrom && isSyntheticItemId(Number(itemIdToStartFrom))) {
        // Resolved by `getPlanner` above once the synthetic items exist.
        return
      }

      if (itemIdToStartFrom) {
        setIsEmpty(IS_EMPTY_DEFAULT_STATE)
        try {
          const item = await api.learningItems.getItemById({
            urlParams: {
              userPlannerItemId: itemIdToStartFrom,
            },
          })
          setCurrentItem(item)
          // return if we found the item
          return
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          // in case we don't find the item due to invalid ID or whatever reason, we just let the code continue and get the last unlocked item
          console.error("> Cannot get item by id, trying to get latest item")
        }
      }
      try {
        const item = await api.learningItems.getLastUnlockedItem({
          urlParams: { learningItemId: plannerId },
        })
        setCurrentItem(item)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        setIsEmpty(p => ({ ...p, item: true }))
      }
    }

    if (plannerId) {
      getPlanner(plannerId)
      getItem(plannerId)
    }
  }, [plannerId, itemIdToStartFrom, isBEv2Module])

  // Keeps the 2 synthetic items' `status` live so the sidebar icons and the
  // milestone progress count (both derived purely from `planner`) stay correct.
  useEffect(() => {
    const syncSyntheticItemStatuses = () => {
      if (!isBEv2Module || !planner) return

      const { aiInterviewStatus, cpatStatus } = deriveSyntheticItemStatuses({
        launchData: launchQuery.data,
        cpatProgress: cpat.latestCPAT?.progress ?? 0,
      })

      setPlanner(prev => {
        if (!prev) return prev

        let changed = false
        const next = prev.map(item => {
          if (
            item.id === SYNTHETIC_ITEM_IDS.aiInterview &&
            item.status !== aiInterviewStatus
          ) {
            changed = true
            return { ...item, status: aiInterviewStatus }
          }
          if (
            item.id === SYNTHETIC_ITEM_IDS.cpat &&
            item.status !== cpatStatus
          ) {
            changed = true
            return { ...item, status: cpatStatus }
          }
          return item
        })

        return changed ? next : prev
      })
    }

    syncSyntheticItemStatuses()
  }, [isBEv2Module, launchQuery.data, cpat.latestCPAT?.progress, planner])

  const updatePlannerList = (item: LearningItem) => {
    // @ts-expect-error this is safe to do
    setPlanner(p => {
      if (!p) return p

      const newItems = p.map(x => {
        if (x.id === item.id) {
          return {
            ...x,
            ...item,
          }
        }
        return x
      })

      return newItems
    })
  }
  /**
   * Updates status in both planner items (sidebar) and the current item
   */
  const hydrateStatusUpdate = (
    userPlannerItemId: number,
    status: LearningItem["status"]
  ) => {
    if (!planner) return
    const newItems = planner.map(x => {
      if (x.id === userPlannerItemId) {
        return { ...x, status }
      }
      return x
    })

    setPlanner(newItems)

    if (!currentItem) return
    // Edge case just in case currentItem got changed until the state update was called
    if (currentItem.id !== userPlannerItemId) return

    setCurrentItem({ ...currentItem, status })
  }

  const getMilestoneInstructions = async (id: number) => {
    try {
      const res = await api.learningItems.getArticle({
        urlParams: {
          articleId: id,
        },
      })
      setMilestoneInstructions(res)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      console.error("> Unable to fetch milestone instructions")
    }
  }

  // Looks for an earlier item (by planner order, up to and including
  // `currentIndex`) that isn't done yet. Used to block jumping ahead -
  // whether the backend rejects it (real items) or there's no backend call
  // to reject it (synthetic items).
  const findPrevIncompleteItem = (
    currentIndex: number
  ): LearningItem | null => {
    if (currentIndex < 0 || !planner) return null
    return (
      planner
        .slice(0, currentIndex + 1)
        .find(
          item =>
            item.status === "unlocked" ||
            item.status === "in_progress" ||
            item.status === "incomplete"
        ) ?? null
    )
  }

  const alertPendingItem = (prevIncompleteItem: LearningItem) => {
    const itemType = getLearningContentTypeDisplay(
      prevIncompleteItem.learningContentType
    )
    actionAlert.error(({ handleClose }) => ({
      body: (
        <div>
          <Typography className="mt-3 text-title4 sm:text-title3">
            Please complete the pending {itemType} before going forward in the
            course
          </Typography>
          <Button
            autoFocus
            className="mt-2 capitalize"
            size="sm"
            color="critical"
            onClick={() => {
              handleClose()
              switchItem({
                mode: "jump",
                userPlannerItemId: prevIncompleteItem.id,
              })
            }}
          >
            Go to {itemType}
          </Button>
        </div>
      ),
      className: "p-0",
    }))
  }

  const switchItem = async (options: SwitchItemOptions) => {
    setLoading(true)
    let item: LearningItem | undefined
    switch (options.mode) {
      case "next":
        if (!currentItem) return

        if (isSyntheticItemId(currentItem.id)) {
          const currentIndex =
            planner?.findIndex(i => i.id === currentItem.id) ?? -1

          const prevIncompleteItem = findPrevIncompleteItem(currentIndex)
          if (prevIncompleteItem) {
            alertPendingItem(prevIncompleteItem)
            break
          }

          const nextItem =
            currentIndex >= 0 ? planner?.[currentIndex + 1] : undefined

          updatePlannerList({ ...currentItem, status: "completed" })

          if (nextItem) {
            item = nextItem
          } else {
            setCurrentItem(p => (p ? { ...p, status: "completed" } : p))
            setShowPlannerCompletedSheet(true)
          }
          break
        }

        try {
          const res = await api.learningItems.getNextItem({
            urlParams: {
              userPlannerItemId: currentItem.id,
            },
          })

          if ("plannerCompleted" in res) {
            const firstSyntheticItem = planner?.find(
              i => i.id === SYNTHETIC_ITEM_IDS.aiInterview
            )

            setCurrentItem(p => {
              if (!p) return p
              const updated: LearningItem = { ...p, status: "completed" }
              updatePlannerList(updated)
              return updated
            })

            if (firstSyntheticItem) {
              item = firstSyntheticItem
            } else {
              setShowPlannerCompletedSheet(true)
            }
          } else {
            setCurrentItem(p => {
              if (!p) return p

              const updated: LearningItem = { ...p, status: "completed" }

              updatePlannerList(updated)
              return updated
            })

            item = res
            const newItemMilestoneId = planner?.find(
              ({ id }) => item!.id === id
            )?.milestone.id
            const currentItemMilestoneId = planner?.find(
              ({ id }) => currentItem.id === id
            )?.milestone.id

            if (
              currentItemMilestoneId &&
              newItemMilestoneId &&
              currentItemMilestoneId !== newItemMilestoneId
            ) {
              getMilestoneInstructions(newItemMilestoneId)
            }
          }

          // Fetch milestone instructions and show that here
          // if (item.milestone.id !== currentItem.milestone.id) {
          //   // Open milestone instruction dialog
          //   setMilestoneInstructions(currentItem)
          // }
        } catch (e) {
          console.error(e)

          const errorMessages = [
            "Cannot complete current item",
            "Cannot unlock item",
          ]

          const currentIndex =
            planner?.findIndex(item => item.id === currentItem?.id) ?? -1

          const prevIncompleteItem = findPrevIncompleteItem(currentIndex)

          if (!prevIncompleteItem) {
            mapErrorsV2(e)
            return
          }

          if (
            e instanceof APIError &&
            e.errors.message &&
            errorMessages.includes(e.errors.message)
          ) {
            alertPendingItem(prevIncompleteItem)
          }
        }
        break
      case "previous":
        if (!currentItem) return

        if (isSyntheticItemId(currentItem.id)) {
          const currentIndex =
            planner?.findIndex(i => i.id === currentItem.id) ?? -1
          item = currentIndex > 0 ? planner?.[currentIndex - 1] : undefined
          break
        }

        try {
          item = await api.learningItems.getPreviousItem({
            urlParams: {
              userPlannerItemId: currentItem.id,
            },
          })
        } catch (e) {
          toast.error("Unable to get previous item", {
            description: "Please contact care@suraasa.com",
          })
          console.error(e)
        }
        break
      case "jump":
        if (isSyntheticItemId(options.userPlannerItemId)) {
          item = planner?.find(i => i.id === options.userPlannerItemId)
          if (!item) {
            toast.error("Item not found")
          }
          break
        }

        try {
          item = await api.learningItems.getItemById({
            urlParams: {
              userPlannerItemId: options.userPlannerItemId,
            },
          })
        } catch (e) {
          toast.error("Item not found")
          console.error(e)
        }
        break
    }

    if (item) {
      setCurrentItem(item)
      updatePlannerList(item)
    }
    setLoading(false)
  }

  const handleArticleBookmark = async (itemId: string) => {
    if (!currentItem || !courseId) return
    if (currentItem.learningContentType !== "article") return
    if (currentItem.uuid !== itemId) return

    const alreadyBookmarked = currentItem.bookmarkArticle.length > 0

    if (alreadyBookmarked) {
      try {
        await api.learningItems.removeArticleBookmark({
          urlParams: { courseId, articleId: itemId },
        })

        const newItem = {
          ...currentItem,
          bookmarkArticle: currentItem.bookmarkArticle.filter(
            x => x.course !== courseId
          ),
        }

        setCurrentItem(newItem)
        updatePlannerList(newItem)

        return
      } catch (e) {
        console.error(e)
      }
    }

    try {
      const bookmark = await api.learningItems.createArticleBookmark({
        // Empty comment is intentional. We are removing the feature of adding a note on articles.

        data: { course: courseId, article: itemId, comment: "bookmarked" },
      })

      const formattedBookmark = {
        uuid: bookmark.uuid,
        comment: bookmark.comment,

        user: AuthManager.getPlatformAuth()!.user.uuid,
        course: bookmark.course.uuid,
        article: bookmark.article.uuid,
      }

      const newItem = {
        ...currentItem,
        bookmarkArticle: [formattedBookmark, ...currentItem.bookmarkArticle],
      }

      setCurrentItem(newItem)
      updatePlannerList(newItem)
    } catch (e) {
      console.error(e)
    }
  }

  const addVideoBookmarkInState = (bookmark: VideoBookmark) => {
    if (!currentItem) return
    if (currentItem.learningContentType !== "video") return

    const newItem = {
      ...currentItem,
      bookmarkVideo: [bookmark, ...currentItem.bookmarkVideo],
    }

    setCurrentItem(newItem)
    updatePlannerList(newItem)
  }

  const removeVideoBookmarkFromState = (bookmarkId: string) => {
    if (!currentItem) return
    if (currentItem.learningContentType !== "video") return

    const newItem = {
      ...currentItem,
      bookmarkVideo: currentItem.bookmarkVideo.filter(
        x => x.uuid !== bookmarkId
      ),
    }

    setCurrentItem(newItem)
    updatePlannerList(newItem)
  }

  const handleDismissMilestoneInstructions = () => {
    setMilestoneInstructions(null)
  }

  return {
    planner,
    currentItem,
    switchItem,
    loading,
    isEmpty,
    removeVideoBookmarkFromState,
    hydrateStatusUpdate,
    handleArticleBookmark,
    addVideoBookmarkInState,
    milestoneInstructions,
    handleDismissMilestoneInstructions,
    totalMilestones,
    currentMilestoneNumber,
    showPlannerCompletedSheet,
    setShowPlannerCompletedSheet,
    isFirstItem,
  }
}
